import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Buffer } from "buffer";
import { ethers } from "ethers";
import { signInErrorMessage } from "../../utils/sign-in-error";
import type { MutableRefObject } from "react";
import { EVM_CONFIG, getActiveBaseShieldedPoolAddress } from "../../config";
import { BASE_DERIVATION_TYPED_DATA_VERSION } from "../helpers/baseDerivation";
import type { SpendingKeys } from "../../joinsplit/types";
import {
  buildAccountLoginClaimFromSpendingKeys,
  challengeMessageWithViewingKey,
  computeViewingKeyDigest,
} from "../../joinsplit/viewingKeyClaim";
import {
  readStoredProxyAuthToken,
  proxyAuthNetworkScope,
  revokeProxyAuthSession,
  storeProxyAuthToken,
  tryRefreshProxyAuthToken,
} from "../../utils/proxy-auth";

// Tokens are scoped by network: the legacy Base deployment keeps its keys,
// a registry network (DogeOS Chikyu) gets its own.
const PROXY_AUTH_NETWORK_SCOPE = proxyAuthNetworkScope();
const BASE_PROXY_AUTH_TOKEN_KEY_PREFIX = `stealthos_proxy_auth_token:${PROXY_AUTH_NETWORK_SCOPE}`;
const BASE_PROXY_AUTH_TOKEN_EXP_KEY_PREFIX = `stealthos_proxy_auth_token_exp:${PROXY_AUTH_NETWORK_SCOPE}`;
const BASE_PROXY_DERIVATION_SIG_LEGACY_PREFIX =
  "stealthos_proxy_derivation_sig_b64:base";

interface UseBaseProxyAuthParams {
  address: string;
  signer: ethers.Signer | null;
  spendingKeys: SpendingKeys | null;
  proxyAuthInFlightRef: MutableRefObject<Promise<string> | null>;
  derivationSignatureB64Ref: MutableRefObject<string>;
}

export interface BaseProxyAuthRequestOptions {
  interactive?: boolean;
  expectedRecipientKey?: string;
}

function normalizeRecipientKey(recipientKey?: string | null): string {
  return String(recipientKey || "")
    .trim()
    .toLowerCase()
    .replace(/^0x/, "");
}

function readTokenRecipientKey(token: string): string {
  try {
    const payload = JSON.parse(atob(token.split(".")[1] || "")) as {
      recipientKey?: string;
    };
    return normalizeRecipientKey(payload.recipientKey);
  } catch {
    return "";
  }
}

function buildEngineUrl(basePath: string, endpoint: string): string {
  if (basePath.includes("?path=")) {
    return `${basePath}${endpoint.replace(/^\//, "")}`;
  }
  return `${basePath.replace(/\/+$/, "")}${endpoint}`;
}

function purgeStoredBaseDerivationSignatures() {
  if (typeof window === "undefined") return;
  for (let i = window.sessionStorage.length - 1; i >= 0; i -= 1) {
    const key = window.sessionStorage.key(i) || "";
    if (key.startsWith(BASE_PROXY_DERIVATION_SIG_LEGACY_PREFIX)) {
      window.sessionStorage.removeItem(key);
    }
  }
}

/** Data-service paths the proxy accepts only with the wallet's bearer token.
 * The sync head and the chain feed are the balance refresh's fast path: a
 * request without the token is refused with 401 and the wallet silently
 * falls back to a full validation. */
const PROXY_AUTH_PATH_PREFIXES = [
  "/api/v1/notes",
  "/api/v1/history",
  "/api/v1/commitments",
  "/api/v1/perps/account",
  "/api/v1/recovery",
  "/api/v1/sync",
];
export function proxyAuthPathNeedsToken(path: string): boolean {
  return PROXY_AUTH_PATH_PREFIXES.some((prefix) => path.startsWith(prefix));
}

export function useBaseProxyAuth({
  address,
  signer,
  spendingKeys,
  proxyAuthInFlightRef,
  derivationSignatureB64Ref,
}: UseBaseProxyAuthParams) {
  const normalizedAddress = useMemo(
    () => address.trim().toLowerCase(),
    [address],
  );
  const keySuffix = normalizedAddress || "unknown";
  const authScopeSuffix = useMemo(
    () =>
      [
        BASE_DERIVATION_TYPED_DATA_VERSION,
        EVM_CONFIG.CHAIN_ID,
        EVM_CONFIG.SETTLEMENT_STACK,
        EVM_CONFIG.BASE_VERIFICATION_MODE,
        getActiveBaseShieldedPoolAddress(),
        EVM_CONFIG.JOINSPLIT_V2.ENABLED
          ? "flat-joinsplit-v2"
          : EVM_CONFIG.CONTRACTS.NOVA_VERIFIER || "no-verifier",
      ]
        .map((part) => String(part).trim().toLowerCase())
        .join(":"),
    [],
  );
  const tokenKey = `${BASE_PROXY_AUTH_TOKEN_KEY_PREFIX}:${keySuffix}:${authScopeSuffix}`;
  const tokenExpKey = `${BASE_PROXY_AUTH_TOKEN_EXP_KEY_PREFIX}:${keySuffix}:${authScopeSuffix}`;
  const activeRecipientKey = useMemo(
    () =>
      spendingKeys
        ? ethers
            .sha256(ethers.toUtf8Bytes(ethers.toBeHex(spendingKeys.mpk, 32)))
            .slice(2)
        : "",
    [spendingKeys],
  );
  const identity = `${normalizedAddress}:${authScopeSuffix}:${activeRecipientKey}`;
  const currentIdentityRef = useRef({ identity, generation: 0 });
  if (currentIdentityRef.current.identity !== identity) {
    currentIdentityRef.current = {
      identity,
      generation: currentIdentityRef.current.generation + 1,
    };
  }
  const generation = currentIdentityRef.current.generation;
  const inFlightGenerationRef = useRef(-1);
  const previousIdentityRef = useRef(identity);
  const prevAddressRef = useRef<string>("");
  const [proxyAuthReady, setProxyAuthReady] = useState(false);
  const [proxyAuthPending, setProxyAuthPending] = useState(false);
  const [proxyAuthError, setProxyAuthError] = useState<string | null>(null);

  const refreshProxyAuthState = useCallback(() => {
    const token = readStoredProxyAuthToken(tokenKey, tokenExpKey);
    const ready =
      !!normalizedAddress &&
      !!activeRecipientKey &&
      !!token &&
      readTokenRecipientKey(token) === activeRecipientKey;
    setProxyAuthReady(ready);
    if (ready) setProxyAuthError(null);
    return ready;
  }, [normalizedAddress, activeRecipientKey, tokenExpKey, tokenKey]);

  const clearStoredAuthArtifacts = useCallback(() => {
    if (typeof window !== "undefined") {
      window.sessionStorage.removeItem(tokenKey);
      window.sessionStorage.removeItem(tokenExpKey);
      window.sessionStorage.removeItem(
        `${BASE_PROXY_AUTH_TOKEN_KEY_PREFIX}:${keySuffix}`,
      );
      window.sessionStorage.removeItem(
        `${BASE_PROXY_AUTH_TOKEN_EXP_KEY_PREFIX}:${keySuffix}`,
      );
      purgeStoredBaseDerivationSignatures();
    }
    setProxyAuthReady(false);
    setProxyAuthError(null);
  }, [keySuffix, tokenExpKey, tokenKey]);

  useEffect(() => {
    if (previousIdentityRef.current === identity) return;
    previousIdentityRef.current = identity;
    const token = readStoredProxyAuthToken(tokenKey, tokenExpKey);
    if (
      !activeRecipientKey ||
      readTokenRecipientKey(token) !== activeRecipientKey
    )
      clearStoredAuthArtifacts();
    else refreshProxyAuthState();
    setProxyAuthPending(false);
  }, [
    identity,
    activeRecipientKey,
    tokenKey,
    tokenExpKey,
    clearStoredAuthArtifacts,
    refreshProxyAuthState,
  ]);

  useEffect(() => {
    purgeStoredBaseDerivationSignatures();
  }, []);

  useEffect(() => {
    refreshProxyAuthState();
    if (typeof window === "undefined") return;
    const interval = window.setInterval(refreshProxyAuthState, 15_000);
    return () => window.clearInterval(interval);
  }, [refreshProxyAuthState]);

  useEffect(() => {
    if (prevAddressRef.current === normalizedAddress) return;
    const previousAddress = prevAddressRef.current;
    prevAddressRef.current = normalizedAddress;
    // Preserve the freshly-derived signature on first connect ('' -> address),
    // but reset when switching wallets or disconnecting.
    const walletChanged =
      previousAddress !== "" && previousAddress !== normalizedAddress;
    const walletDisconnected =
      previousAddress !== "" && normalizedAddress === "";
    if (walletChanged || walletDisconnected) {
      derivationSignatureB64Ref.current = "";
      proxyAuthInFlightRef.current = null;
      clearStoredAuthArtifacts();
    }
  }, [
    normalizedAddress,
    derivationSignatureB64Ref,
    proxyAuthInFlightRef,
    clearStoredAuthArtifacts,
  ]);

  const ensureProxyAuthToken = useCallback(
    async (options?: BaseProxyAuthRequestOptions): Promise<string> => {
      const assertCurrent = () => {
        if (currentIdentityRef.current.generation !== generation)
          throw new Error(
            "Private account changed during sign-in; retry with the current wallet",
          );
      };
      assertCurrent();
      if (!address || !signer) {
        setProxyAuthReady(false);
        throw new Error(
          "Wallet signature is required for protected note/history access",
        );
      }
      if (!spendingKeys || !activeRecipientKey)
        throw new Error("Unlock the private wallet before signing in");
      const requestedRecipientKey = normalizeRecipientKey(
        options?.expectedRecipientKey,
      );
      if (requestedRecipientKey && requestedRecipientKey !== activeRecipientKey)
        throw new Error(
          "Requested private account does not match the unlocked wallet",
        );
      const expectedRecipientKey = activeRecipientKey;

      if (
        proxyAuthInFlightRef.current &&
        inFlightGenerationRef.current === generation
      ) {
        try {
          const token = await proxyAuthInFlightRef.current;
          assertCurrent();
          const tokenRecipientKey = readTokenRecipientKey(token);
          if (tokenRecipientKey !== expectedRecipientKey) {
            setProxyAuthReady(false);
            throw new Error(
              "Proxy auth token is for a different private account; please sign in again",
            );
          }
          setProxyAuthReady(true);
          setProxyAuthError(null);
          return token;
        } catch (error) {
          assertCurrent();
          setProxyAuthReady(false);
          if (options?.interactive === true) {
            setProxyAuthError(
              error instanceof Error ? error.message : String(error),
            );
          }
          throw error;
        }
      }

      const existingToken = readStoredProxyAuthToken(tokenKey, tokenExpKey);
      if (existingToken) {
        const tokenRecipientKey = readTokenRecipientKey(existingToken);
        if (tokenRecipientKey !== expectedRecipientKey) {
          clearStoredAuthArtifacts();
        } else {
          setProxyAuthReady(true);
          setProxyAuthError(null);
          return existingToken;
        }
      }

      const inFlight = (async () => {
        if (options?.interactive === true) {
          setProxyAuthPending(true);
          setProxyAuthError(null);
        }
        const wallet = address;
        const refreshData = await tryRefreshProxyAuthToken(wallet);
        assertCurrent();
        if (refreshData?.token) {
          const refreshRecipientKey = readTokenRecipientKey(refreshData.token);
          if (refreshRecipientKey !== expectedRecipientKey) {
            clearStoredAuthArtifacts();
            if (options?.interactive !== true) {
              throw new Error(
                "Proxy auth session is for a different private account; please sign in again",
              );
            }
          } else {
            storeProxyAuthToken(
              tokenKey,
              tokenExpKey,
              refreshData.token,
              refreshData.expiresAt,
            );
            return refreshData.token;
          }
        }

        if (options?.interactive !== true) {
          setProxyAuthReady(false);
          throw new Error("Proxy auth token unavailable");
        }

        const nonceResponse = await fetch("/api/auth/nonce", {
          method: "POST",
          credentials: "same-origin",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            wallet,
            ...(EVM_CONFIG.NETWORK?.registryKey
              ? { network: EVM_CONFIG.NETWORK.registryKey }
              : {}),
          }),
        });
        if (!nonceResponse.ok) {
          const err = await nonceResponse.text();
          throw new Error(
            `Proxy auth nonce failed: ${err || nonceResponse.status}`,
          );
        }
        const nonceData = (await nonceResponse.json()) as {
          message: string;
          nonce: string;
          derivationTypedData?: unknown;
          challenge: string;
        };
        assertCurrent();

        if (!spendingKeys) {
          throw new Error("Unlock the private wallet before signing in");
        }

        // Prove the active private account, not an account re-derived from the
        // latest wallet signature: recovery can restore a different seed.
        // Only public claim fields and a per-challenge ownership proof leave
        // the browser; the spending key and setup signature remain local.
        const { claim: viewingKey, proof: accountProof } =
          await buildAccountLoginClaimFromSpendingKeys(
            spendingKeys,
            wallet,
            String(nonceData.nonce || ""),
          );
        const viewingKeyDigest = await computeViewingKeyDigest(viewingKey);
        assertCurrent();

        const challengeSigHex = await signer.signMessage(
          challengeMessageWithViewingKey(nonceData.message, viewingKeyDigest),
        );
        const signatureB64 = Buffer.from(
          ethers.getBytes(challengeSigHex),
        ).toString("base64");
        assertCurrent();

        const verifyResponse = await fetch("/api/auth/verify", {
          method: "POST",
          credentials: "same-origin",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            wallet,
            challenge: nonceData.challenge,
            signature: signatureB64,
            viewingKey,
            accountProof,
          }),
        });
        if (!verifyResponse.ok) {
          const err = await verifyResponse.text();
          throw new Error(signInErrorMessage(err));
        }

        const verifyData = (await verifyResponse.json()) as {
          token: string;
          expiresAt?: number | null;
          recipientKey?: string;
          recoveryKey?: string;
        };
        assertCurrent();
        if (!verifyData.token) {
          throw new Error("Proxy auth verify returned no token");
        }
        const verifyRecipientKey = readTokenRecipientKey(verifyData.token);
        if (verifyRecipientKey !== expectedRecipientKey) {
          clearStoredAuthArtifacts();
          derivationSignatureB64Ref.current = "";
          revokeProxyAuthSession(wallet);
          throw new Error(
            "Proxy auth recipient did not match the connected private account; reconnect and sign the Base privacy-key request again",
          );
        }

        storeProxyAuthToken(
          tokenKey,
          tokenExpKey,
          verifyData.token,
          verifyData.expiresAt,
        );

        if (verifyData.recipientKey && verifyData.recoveryKey) {
          try {
            const registration = await fetch(
              buildEngineUrl(
                EVM_CONFIG.PROVER_URL,
                "/api/v1/recovery/register",
              ),
              {
                method: "POST",
                headers: {
                  "Content-Type": "application/json",
                  Authorization: `Bearer ${verifyData.token}`,
                },
                body: JSON.stringify(
                  EVM_CONFIG.SETTLEMENT_STACK === "flat_joinsplit_v2"
                    ? {
                        recoveryVersion: 2,
                        chainId: EVM_CONFIG.CHAIN_ID,
                        poolAddress: getActiveBaseShieldedPoolAddress(),
                        recipient_key: verifyData.recipientKey,
                      }
                    : {
                        recipient_key: verifyData.recipientKey,
                        recovery_key: verifyData.recoveryKey,
                      },
                ),
              },
            );
            if (!registration.ok)
              console.warn(
                "[base-proxy-auth] Recovery registration is not ready; check the recovery panel before funding.",
              );
          } catch {
            console.warn(
              "[base-proxy-auth] Recovery registration is unavailable; check the recovery panel before funding.",
            );
          }
        }

        return verifyData.token;
      })();

      inFlightGenerationRef.current = generation;
      proxyAuthInFlightRef.current = inFlight;
      try {
        const token = await inFlight;
        assertCurrent();
        setProxyAuthReady(true);
        setProxyAuthError(null);
        return token;
      } catch (error) {
        assertCurrent();
        setProxyAuthReady(false);
        if (options?.interactive === true) {
          setProxyAuthError(
            error instanceof Error ? error.message : String(error),
          );
        }
        throw error;
      } finally {
        if (proxyAuthInFlightRef.current === inFlight)
          proxyAuthInFlightRef.current = null;
        if (
          currentIdentityRef.current.generation === generation &&
          options?.interactive === true
        ) {
          setProxyAuthPending(false);
        }
      }
    },
    [
      address,
      signer,
      spendingKeys,
      activeRecipientKey,
      generation,
      proxyAuthInFlightRef,
      derivationSignatureB64Ref,
      tokenKey,
      tokenExpKey,
      clearStoredAuthArtifacts,
    ],
  );

  const authHeadersForPath = useCallback(
    async (
      path: string,
      options?: BaseProxyAuthRequestOptions,
    ): Promise<Record<string, string>> => {
      const pathOnly = path.split("?")[0];
      const requiresProtectedWriteAuth =
        pathOnly === "/api/v1/history/store" ||
        pathOnly === "/api/v1/notes/spent" ||
        pathOnly === "/api/v1/notes/promote" ||
        pathOnly === "/api/v1/notes/archive" ||
        pathOnly.startsWith("/api/v1/recovery/journal") ||
        pathOnly === "/api/v1/recovery/register" ||
        pathOnly.startsWith("/api/v1/commitments");
      if (!proxyAuthPathNeedsToken(path)) {
        return {};
      }
      try {
        const token = await ensureProxyAuthToken(options);
        return { Authorization: `Bearer ${token}` };
      } catch (error) {
        if (options?.interactive === true || requiresProtectedWriteAuth) {
          throw error;
        }
        return {};
      }
    },
    [ensureProxyAuthToken],
  );

  const clearProxyAuthToken = useCallback(() => {
    clearStoredAuthArtifacts();
    derivationSignatureB64Ref.current = "";
    proxyAuthInFlightRef.current = null;
    revokeProxyAuthSession(address);
  }, [
    address,
    clearStoredAuthArtifacts,
    derivationSignatureB64Ref,
    proxyAuthInFlightRef,
  ]);

  return {
    ensureProxyAuthToken,
    authHeadersForPath,
    clearProxyAuthToken,
    proxyAuthReady,
    proxyAuthPending,
    proxyAuthError,
    refreshProxyAuthState,
  };
}
